import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronRight, Layers, FileText, Search as SearchIcon } from "lucide-react";

export default function HomePage() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/categories");
                setGroups(data);
                setTotal(data.reduce((s, g) => s + g.count, 0));
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="flex-1 overflow-y-auto scroll-thin" data-testid="home-page">
            {/* Hero */}
            <section className="bg-gradient-to-b from-white to-medical-50 border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-10 sm:py-14 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                    <div className="lg:col-span-7">
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-medical-900 tracking-tight leading-[1.05]">
                            ED Whiteboard.
                            <span className="block text-teal-700">
                                Pathways, protocols, and charts at your fingertips.
                            </span>
                        </h1>
                        <p className="mt-5 max-w-xl text-medical-800 text-base sm:text-lg leading-relaxed">
                            A digital edition of the Emergency Department reference manual for
                            Watford General Hospital. Click any title to jump to its page, or search
                            across the entire document.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3 text-xs">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-medical-800">
                                <FileText className="h-3.5 w-3.5 text-teal-700" />
                                <span className="font-mono">{total}</span> pages
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-medical-800">
                                <Layers className="h-3.5 w-3.5 text-teal-700" />
                                <span className="font-mono">{groups.length}</span> categories
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-medical-800">
                                <SearchIcon className="h-3.5 w-3.5 text-teal-700" />
                                Full-text search
                            </span>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <figure
                            data-testid="hero-figure"
                            className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-medical-900/10 bg-medical-900"
                        >
                            <img
                                src="/images/ed-entrance.jpg"
                                alt="Accident & Emergency / Acute Admissions Unit entrance, Watford General Hospital"
                                className="w-full h-auto block"
                                loading="eager"
                            />
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-medical-900/85 via-medical-900/40 to-transparent p-4 sm:p-5">
                                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-teal-300 mb-1">
                                    Watford General Hospital
                                </div>
                                <div className="font-display text-sm sm:text-base font-semibold text-white leading-tight">
                                    Emergency Department · Acute Admissions Unit
                                </div>
                                <div className="text-[11px] text-medical-200 mt-0.5">
                                    West Hertfordshire Teaching Hospitals NHS Trust
                                </div>
                            </div>
                        </figure>
                        <p className="mt-3 text-[11px] font-mono text-slate-400 text-right">
                            By Chinyere V Patrick Onyeaka · For internal use only
                        </p>
                    </div>
                </div>
            </section>

            {/* TOC */}
            <section className="max-w-5xl mx-auto px-6 sm:px-8 py-10">
                <div className="flex items-baseline justify-between mb-6">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-medical-900">
                        Table of Contents
                    </h2>
                    <p className="text-xs font-mono text-slate-500">
                        Click any title to open the page.
                    </p>
                </div>

                {loading && (
                    <div className="py-16 text-center text-sm text-slate-500" data-testid="home-loading">
                        Loading table of contents…
                    </div>
                )}

                {!loading && total === 0 && (
                    <div
                        data-testid="home-empty"
                        className="py-16 text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg"
                    >
                        No pages have been added yet. Visit the{" "}
                        <Link to="/admin" className="text-teal-700 underline">
                            admin panel
                        </Link>{" "}
                        to upload some.
                    </div>
                )}

                <div className="space-y-10">
                    {groups.map((g) => (
                        <div key={g.category} data-testid={`category-block-${g.category}`}>
                            <div className="flex items-baseline justify-between border-b border-slate-200 pb-2 mb-3">
                                <h3 className="font-display text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
                                    {g.category}
                                </h3>
                                <span className="text-[10px] font-mono text-slate-400">
                                    {String(g.count).padStart(2, "0")} pages
                                </span>
                            </div>
                            <ul className="grid sm:grid-cols-2 gap-x-8">
                                {g.pages.map((p) => (
                                    <li key={p.id}>
                                        <Link
                                            data-testid={`home-toc-link-${p.id}`}
                                            to={`/p/${p.id}`}
                                            className="group flex items-baseline gap-3 py-2 border-b border-dashed border-slate-200 hover:border-teal-700 transition-colors"
                                        >
                                            <span className="font-mono text-[10px] text-slate-400 w-9 flex-shrink-0">
                                                {String(p.page_number).padStart(3, "0")}
                                            </span>
                                            <span className="flex-1 text-sm text-medical-900 group-hover:text-teal-800 transition-colors leading-snug">
                                                {p.title}
                                            </span>
                                            <ChevronRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-teal-700 transition-colors" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            <footer className="border-t border-slate-200 py-8 px-6 text-center text-xs text-slate-500 font-mono">
                Internal reference. For training & quick-lookup use only.
            </footer>
        </div>
    );
}
